import { BrowserDataCacheSourceAdapter } from '../../core/source-adapter';
import { BrowserDataCacheSqlSchema } from '../../sqlite/schema';
import { BrowserDataCacheTableMapping } from '../../sqlite/table-mapping';

export interface FlowStudioFlowApiItem {
  name?: string;
  id?: string;
  environment?: string;
  properties?: {
    displayName?: string;
    state?: string;
    lastModifiedTime?: string;
    definitionSummary?: {
      triggers?: unknown[];
      actions?: unknown[];
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface FlowStudioFlowRow {
  key: string;
  flowName: string;
  environment: string;
  displayName: string;
  state: string;
  modifiedAt: string | null;
  rawJson: string;
}

export interface FlowStudioFlowSourceOptions<TCursor = unknown> {
  id?: string;
  environment: string;
  scope?: 'user' | 'admin';
  listPage: (cursor?: TCursor | null) => Promise<{ items: FlowStudioFlowApiItem[]; nextCursor?: TCursor | null; total?: number }>;
}

export const flowStudioFlowSchema: BrowserDataCacheSqlSchema = {
  id: 'flow-studio-flows',
  tables: [{
    name: 'flows',
    createSql: `
      CREATE TABLE IF NOT EXISTS flows (
        key TEXT PRIMARY KEY,
        flowName TEXT NOT NULL,
        environment TEXT NOT NULL,
        displayName TEXT NOT NULL,
        state TEXT,
        modifiedAt TEXT,
        rawJson TEXT NOT NULL
      );
    `,
  }],
  views: [{
    name: 'flow_search_view',
    createSql: `
      CREATE VIEW flow_search_view AS
      SELECT key, flowName, environment, displayName, state, modifiedAt
      FROM flows;
    `,
  }],
};

export const flowStudioFlowTableMapping: BrowserDataCacheTableMapping<FlowStudioFlowRow> = {
  table: 'flows',
  primaryKey: ['key'],
  columns: ['key', 'flowName', 'environment', 'displayName', 'state', 'modifiedAt', 'rawJson'],
  toColumnValues: (row) => ({
    key: row.key,
    flowName: row.flowName,
    environment: row.environment,
    displayName: row.displayName,
    state: row.state,
    modifiedAt: row.modifiedAt,
    rawJson: row.rawJson,
  }),
};

/**
 * Returns the environment partition key used for storage.
 * Admin flows are stored under `${environment}:admin` so they never
 * collide with user flows in the same table, without any schema change.
 * Existing user-scope data (keys written as `${environment}/${flowName}`)
 * continues to work unchanged.
 */
export function resolveFlowEnvironmentKey(environment: string, scope: 'user' | 'admin'): string {
  return scope === 'admin' ? `${environment}:admin` : environment;
}

export function createFlowStudioFlowSource<TCursor = unknown>(
  options: FlowStudioFlowSourceOptions<TCursor>,
): BrowserDataCacheSourceAdapter<FlowStudioFlowApiItem, FlowStudioFlowRow, TCursor> {
  const envKey = resolveFlowEnvironmentKey(options.environment, options.scope ?? 'user');
  return {
    id: options.id || 'flow-studio.flows',
    table: 'flows',
    listPage: async (_context, cursor) => options.listPage(cursor),
    getModifiedAt: (item) => item.properties?.lastModifiedTime || null,
    mapRow: (item) => mapFlowStudioFlowRow(envKey, item),
  };
}

export function mapFlowStudioFlowRow(environment: string, item: FlowStudioFlowApiItem): FlowStudioFlowRow | null {
  const flowName = String(item.name || item.id || '').trim();
  if (!flowName) return null;
  const displayName = String(item.properties?.displayName || flowName);
  return {
    key: `${environment}/${flowName}`,
    flowName,
    environment,
    displayName,
    state: String(item.properties?.state || ''),
    modifiedAt: item.properties?.lastModifiedTime || null,
    rawJson: JSON.stringify(item),
  };
}
